import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Button,
  Building2,
  Card,
  ChevronDown,
  FileText,
  Input,
  ProgressBar,
  Select,
  Tags,
  Upload,
} from "../../ui";
import { BillingButton } from "../../subscription/BillingButton";
import "./Completion.css";

/*
  Store completion. Every value is read from, and saved to, the store record
  (details.* / moreDetails.* / contactInformation.*) through the existing completion
  endpoint (PATCH /dashboard/completion/:storeId/store), which writes the store fields;
  a client whose store is still being set up saves through the onboarding draft API
  instead (the caller passes `onComplete`, this component does not know which).
  Contact Information is the store's CONTACT PERSON (`store.contactInformation`): not the
  owner, not the login e-mail (`user.email`) and not the user's own contact e-mail.
  Optional information never counts: the integration section, the tax number and tax
  document, the contact person's job title, and the marketing / accountant contacts.
  Conditional fields count only when they apply. The same rules live in
  lud-backend/services/storeCompletionService.js.
*/

const isEmpty = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const digitsOnly = (value) => String(value).replace(/[\s+()-]/g, "");
const validators = {
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v).trim()),
  phone: (v) => /^\d{8,15}$/.test(digitsOnly(v)),
  iban: (v) => /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(String(v).replace(/\s/g, "").toUpperCase()),
  commercial: (v) => /^\d{10}$/.test(String(v).trim()),
  tax: (v) => /^\d{15}$/.test(String(v).trim()),
  futureDate: (v) => {
    const date = new Date(v);
    if (Number.isNaN(date.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  },
};

// [key, Arabic label, English label, input type, validator, invalid message (ar, en)]
const F = (key, ar, en, type = "text", check = null, msg = null) => ({
  key, ar, en, type, check, msg,
});
// A field that must be filled when its section is saved (the form checks it; the Store model requires it).
const required = (field) => ({ ...field, required: true });

const MSG = {
  email: ["بريد إلكتروني غير صالح", "Invalid email address"],
  phone: ["رقم هاتف غير صالح", "Invalid phone number"],
  iban: ["آيبان غير صالح", "Invalid IBAN"],
  commercial: ["السجل التجاري يجب أن يكون 10 أرقام", "Must be 10 digits"],
  tax: ["الرقم الضريبي يجب أن يكون 15 رقماً", "Must be 15 digits"],
  expired: ["التاريخ منتهي أو غير صالح", "Expired or invalid date"],
  required: ["هذا الحقل مطلوب", "This field is required"],
};

export const SECTIONS = [
  {
    id: "business",
    ar: "معلومات النشاط",
    en: "Business Information",
    fields: [
      F("businessName", "اسم المتجر", "Store Name"),
      F("businessNameEn", "اسم المتجر بالإنجليزية", "English Store Name"),
      F("city", "المدينة", "City"),
      F("type", "التصنيف", "Category"),
      F("entityName", "اسم الجهة / المنشأة", "Entity / Organization"),
      F("commercialNumber", "رقم السجل التجاري", "Commercial Number", "text", validators.commercial, MSG.commercial),
      F("crExpiration", "انتهاء السجل التجاري", "CR Expiration", "date", validators.futureDate, MSG.expired),
      { ...F("taxNumber", "الرقم الضريبي", "Tax Number", "text", validators.tax, MSG.tax), optional: true },
      F("ownerName", "اسم المالك", "Owner Name"),
      F("ownerPhone", "جوال المالك", "Owner Mobile", "tel", validators.phone, MSG.phone),
      F("ownerEmail", "بريد المالك", "Owner Email", "email", validators.email, MSG.email),
    ],
  },
  {
    id: "contact",
    ar: "معلومات التواصل",
    en: "Contact Information",
    fields: [
      required(F("contactName", "اسم جهة التواصل", "Contact Name")),
      required(F("contactPhone", "جوال التواصل", "Contact Phone", "tel", validators.phone, MSG.phone)),
      required(F("contactEmail", "بريد التواصل", "Contact Email", "email", validators.email, MSG.email)),
      { ...F("contactJobTitle", "المسمى الوظيفي", "Job Title"), optional: true },
    ],
  },
  {
    id: "bank",
    ar: "المعلومات البنكية",
    en: "Bank Information",
    fields: [
      F("bankName", "اسم البنك", "Bank Name"),
      F("IBANnum", "رقم الآيبان", "IBAN", "text", validators.iban, MSG.iban),
      F("bankBeneficiaryName", "اسم المستفيد", "Beneficiary Name"),
    ],
  },
  {
    id: "integration",
    optional: true,
    ar: "التكامل والتشغيل",
    en: "Integration & Operations",
    fields: [
      { key: "hasIntegration", ar: "يوجد تكامل؟", en: "Integration Available?", type: "yesno", always: true },
      {
        ...F("integrationName", "اسم التكامل", "Integration Name"),
        when: (v) => v.hasIntegration === "yes",
      },
      { key: "integrationType", ar: "نمط التشغيل", en: "Operation Mode", type: "mode" },
      {
        ...F("posName", "اسم نظام POS", "POS Name"),
        when: (v) => v.integrationType === "pos",
      },
    ],
  },
  {
    id: "marketing",
    optional: true,
    ar: "تسويق",
    en: "Marketing",
    fields: [
      F("salesPhone", "هاتف التسويق", "Marketing Phone", "tel", validators.phone, MSG.phone),
      F("salesEmail", "بريد التسويق", "Marketing Email", "email", validators.email, MSG.email),
    ],
  },
  {
    id: "accountant",
    optional: true,
    ar: "المحاسب",
    en: "Accountant",
    fields: [
      F("accountantPhone", "هاتف المحاسب", "Accountant Phone", "tel", validators.phone, MSG.phone),
      F("accountantEmail", "بريد المحاسب", "Accountant Email", "email", validators.email, MSG.email),
    ],
  },
];

// The documents Completion tracks. `source` tells where the file lives: organization
// documents (the store's LUD document) or the dashboard banner. `optional` documents
// are shown and managed but never counted.
export const COMPLETION_DOCUMENTS = [
  { key: "cr", ar: "السجل التجاري", en: "Commercial Registration", source: "organization" },
  { key: "tax", ar: "مستند الرقم الضريبي", en: "Tax Document", source: "organization", optional: true },
  { key: "iban", ar: "مستند الآيبان", en: "IBAN Document", source: "organization" },
  { key: "license", ar: "تراخيص الفروع", en: "Branch Licenses", source: "organization" },
  { key: "address", ar: "العنوان الوطني", en: "National Address", source: "organization" },
  { key: "logo", ar: "الشعار", en: "Logo", source: "organization", image: true },
  { key: "banner", ar: "البنر", en: "Banner", source: "banner", image: true },
];

// Marketing and Accountant information live in ONE card (one Edit, one Save, one payload): same fields, labels and validation as before.
const MARKETING = SECTIONS.find((section) => section.id === "marketing");
const ACCOUNTANT = SECTIONS.find((section) => section.id === "accountant");
const CHANNELS_CARD = {
  id: "channels",
  optional: true,
  ar: "معلومات التسويق والمحاسب",
  en: "Marketing & Accountant Information",
  groups: [MARKETING, ACCOUNTANT],
  fields: [...MARKETING.fields, ...ACCOUNTANT.fields],
};
const CARDS = SECTIONS.filter((section) => section !== MARKETING && section !== ACCOUNTANT).concat(CHANNELS_CARD);

const STORE_KEYS = SECTIONS.flatMap((section) => section.fields.map((f) => f.key));
// The contact person is saved with its own section only: the other sections never send it (an empty contact would be refused).
const CONTACT_KEYS = SECTIONS.find((section) => section.id === "contact").fields.map((f) => f.key);

// Old completion records kept the form under different names.
const LEGACY_KEYS = {
  commercialNumber: "crNumber",
  businessNameEn: "storeNameEn",
  IBANnum: "iban",
  bankBeneficiaryName: "bankBeneficiary",
  type: "category",
  salesPhone: "marketingPhone",
  salesEmail: "marketingEmail",
};

const dateOnly = (value) => (value ? String(value).slice(0, 10) : "");

/** Current values, read from the store record (legacy completion data fills gaps). */
export const readStoreValues = (store = {}, legacy = {}) => {
  const details = store.details || {};
  const more = store.moreDetails || {};
  const fromStore = {
    businessName: store.businessName || store.name,
    businessNameEn: more.businessNameEn,
    city: store.city,
    type: details.type,
    entityName: details.organization,
    commercialNumber: details.commercialNumber,
    crExpiration: dateOnly(details.crExpiration),
    taxNumber: details.taxNumber,
    ownerName: details.ownerName,
    ownerPhone: details.ownerPhone,
    ownerEmail: details.ownerEmail,
    // canonical contact person; the legacy mirrored fields (name / phone / email) fill a store that was never re-saved
    contactName: store.contactInformation?.name || store.name,
    contactPhone: store.contactInformation?.phone || store.phone,
    contactEmail: store.contactInformation?.email || store.email,
    contactJobTitle: store.contactInformation?.jobTitle,
    bankName: more.bankName,
    IBANnum: more.IBANnum,
    bankBeneficiaryName: more.bankBeneficiaryName,
    integrationName: more.integrationName,
    posName: more.posName,
    salesPhone: more.salesPhone,
    salesEmail: more.salesEmail,
    accountantPhone: more.accountantPhone,
    accountantEmail: more.accountantEmail,
  };
  const values = {};
  STORE_KEYS.forEach((key) => {
    const own = fromStore[key];
    const old = legacy?.[LEGACY_KEYS[key] || key];
    values[key] = !isEmpty(own) ? String(own) : !isEmpty(old) ? String(old) : "";
  });
  values.crExpiration = dateOnly(values.crExpiration);
  const legacyMode = legacy?.integrationType;
  values.integrationType =
    more.integrationType === "pos" || more.integrationType === "external_tablet"
      ? more.integrationType
      : legacyMode === "pos"
        ? "pos"
        : legacyMode === "tablet" || legacyMode === "external_tablet"
          ? "external_tablet"
          : "";
  values.hasIntegration =
    more.hasIntegration || !isEmpty(values.integrationName) ? "yes" : "no";
  return values;
};

/** Status of one field: "complete" | "missing" | "invalid" | "optional" | null (not applicable). */
export const fieldStatus = (field, values, optional = false) => {
  optional = optional || Boolean(field.optional);
  if (field.when && !field.when(values)) return null;
  if (field.always) return "complete";
  const value = values[field.key];
  if (isEmpty(value)) return optional ? "optional" : "missing";
  if (field.check && !field.check(value)) return "invalid";
  return "complete";
};

/**
 * Required fields + the five documents only. Optional sections and the
 * integration yes/no answer never count; conditional fields count when they apply.
 */
export const computeCompletion = (values, documentUploaded = {}) => {
  const requirements = [];
  SECTIONS.forEach((section) => {
    if (section.optional) return;
    section.fields.forEach((field) => {
      if (field.always || field.optional) return;
      const status = fieldStatus(field, values);
      if (status) {
        requirements.push({
          id: `${section.id}-${field.key}`,
          section: section.id,
          field,
          sectionAr: section.ar,
          sectionEn: section.en,
          done: status === "complete",
          status,
        });
      }
    });
  });
  COMPLETION_DOCUMENTS.forEach((doc) => {
    if (doc.optional) return;
    const done = Boolean(documentUploaded[doc.key]);
    requirements.push({
      id: `documents-${doc.key}`,
      section: "documents",
      field: doc,
      sectionAr: "المستندات",
      sectionEn: "Documents",
      done,
      status: done ? "complete" : "missing",
    });
  });
  const total = requirements.length;
  const completed = requirements.filter((r) => r.done).length;
  return {
    requirements,
    total,
    completed,
    missing: requirements.filter((r) => !r.done),
    percent: total ? Math.round((completed / total) * 100) : 0,
  };
};

const fileUrl = (file) => file?.url || file?.secure_url || file?.secureUrl || "";
const isImageFile = (file) =>
  /^image\//i.test(file?.mimeType || "") || file?.kind === "image" ||
  /\.(png|jpe?g|webp|gif|svg)$/i.test(file?.originalName || file?.name || "");

const STATUS_TAG = {
  complete: { variant: "success", ar: "مكتمل", en: "Complete" },
  missing: { variant: "danger", ar: "ناقص", en: "Missing" },
  invalid: { variant: "warning", ar: "غير صالح", en: "Invalid" },
  optional: { variant: "neutral", ar: "اختياري", en: "Optional" },
};

const StatusTag = ({ status, lang }) => {
  const tag = STATUS_TAG[status];
  return tag ? (
    <Tags variant={tag.variant} appearance="soft" size="sm">
      {lang === "ar" ? tag.ar : tag.en}
    </Tags>
  ) : null;
};

export default function Completion({
  lang,
  text,
  items = [],
  store = {},
  organizationDocuments = [],
  banner = null,
  canManageFiles = false,
  readOnly = false, // onboarding under review / approved: the sections are shown, not editable
  onComplete,
  onSaveDocuments,
  onDeleteDocument,
  onOpenDocument,
  onUploadBanner,
  onRemoveBanner,
  onMissingChange,
  needsSubscription = false, // the selected store has no active subscription: say so, with the way to the billing page
}) {
  const ar = lang === "ar";
  const t = (arText, enText) => (ar ? arText : enText);
  const storeItem = items.find((item) => item.id === "store") || { id: "store" };

  // Values saved in this session, shown until the refreshed store arrives.
  const [saved, setSaved] = useState(null);
  useEffect(() => setSaved(null), [store]);

  // Switching store: nothing typed or reported for the previous store may follow it.
  const storeKey = store?.id || store?._id || "";
  useEffect(() => {
    setEditing(null);
    setDraft({});
    setErrors({});
    setSectionError("");
    setDocError("");
    setBusyDoc("");
  }, [storeKey]);

  const committed = useMemo(
    () => ({ ...readStoreValues(store, storeItem.data || {}), ...(saved || {}) }),
    [store, storeItem.data, saved],
  );

  const [editing, setEditing] = useState(null); // section id
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [sectionError, setSectionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyDoc, setBusyDoc] = useState("");
  const [docError, setDocError] = useState("");
  const fileInputs = useRef({});
  const sectionRefs = useRef({});

  const docInfo = useMemo(() => {
    const byKey = new Map(organizationDocuments.map((d) => [d.key, d]));
    return COMPLETION_DOCUMENTS.map((doc) => {
      if (doc.source === "banner") {
        return { ...doc, file: banner?.url ? banner : null, url: banner?.url || "" };
      }
      const record = byKey.get(doc.key);
      return { ...doc, record, file: record?.file || null, url: fileUrl(record?.file) };
    });
  }, [organizationDocuments, banner]);

  const completion = useMemo(
    () => computeCompletion(committed, Object.fromEntries(docInfo.map((d) => [d.key, Boolean(d.file)]))),
    [committed, docInfo],
  );

  useEffect(() => {
    onMissingChange?.(completion.missing.length > 0);
  }, [onMissingChange, completion.missing.length]);

  const startEdit = (section) => {
    setEditing(section.id);
    setDraft(Object.fromEntries(section.fields.map((f) => [f.key, committed[f.key]])));
    setErrors({});
    setSectionError("");
  };
  const cancelEdit = () => {
    setEditing(null);
    setErrors({});
    setSectionError("");
  };

  const saveSection = async (section) => {
    const next = { ...committed, ...draft };
    if (next.hasIntegration === "no") next.integrationName = "";
    if (next.integrationType !== "pos") next.posName = "";
    const found = {};
    section.fields.forEach((field) => {
      const value = next[field.key];
      if (field.required && isEmpty(value)) {
        found[field.key] = ar ? MSG.required[0] : MSG.required[1];
      } else if (field.check && !isEmpty(value) && !field.check(value)) {
        found[field.key] = ar ? field.msg[0] : field.msg[1];
      }
    });
    if (section.id === "business" && isEmpty(next.businessName)) {
      found.businessName = t("اسم المتجر مطلوب", "Store name is required");
    }
    setErrors(found);
    if (Object.keys(found).length) return;
    const payload = {};
    STORE_KEYS.forEach((key) => {
      if (CONTACT_KEYS.includes(key) && section.id !== "contact") return;
      payload[key] = key === "hasIntegration" ? next[key] === "yes" : next[key];
    });
    setSaving(true);
    setSectionError("");
    try {
      await onComplete?.(storeItem, payload);
      setSaved(next);
      setEditing(null);
    } catch (error) {
      setSectionError(error?.message || t("تعذر الحفظ", "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const runDoc = async (key, action) => {
    setBusyDoc(key);
    setDocError("");
    try {
      await action();
    } catch (error) {
      setDocError(error?.message || t("فشلت العملية", "Operation failed"));
    } finally {
      setBusyDoc("");
    }
  };

  const uploadDoc = (doc, file) => {
    if (!file) return;
    if (doc.image && !/^image\//i.test(file.type || "")) {
      setDocError(t("يجب أن يكون الملف صورة", "The file must be an image"));
      return;
    }
    runDoc(doc.key, () =>
      doc.source === "banner"
        ? onUploadBanner?.(file)
        : onSaveDocuments?.({ [doc.key]: file }, null),
    );
  };
  const deleteDoc = (doc) => {
    if (!window.confirm(t("حذف هذا الملف؟", "Delete this file?"))) return;
    runDoc(doc.key, () =>
      doc.source === "banner" ? onRemoveBanner?.() : onDeleteDocument?.(doc.key),
    );
  };
  const viewDoc = (doc) => {
    if (doc.source === "banner") {
      if (doc.url) window.open(doc.url, "_blank", "noopener,noreferrer");
      return;
    }
    runDoc(doc.key, () => onOpenDocument?.(doc.record));
  };

  // Panels stay mounted (only collapsed), so unsaved drafts survive toggling.
  const [openMissing, setOpenMissing] = useState(true);
  const [openAccount, setOpenAccount] = useState(false);

  // Opens Account Information, then scrolls the target card into view.
  const jumpTo = (sectionId) => {
    setOpenAccount(true);
    setTimeout(
      () => sectionRefs.current[sectionId]?.scrollIntoView?.({ behavior: "smooth", block: "start" }),
      280,
    );
  };

  if (!store?.id && !store?._id) {
    return (
      <section className="lud-dashboard-section">
        <Card className="lud-completion-card">
          <p className="lud-completion-empty">
            {t("لا توجد بيانات متجر لعرضها.", "No store data to show.")}
          </p>
        </Card>
      </section>
    );
  }

  const renderValue = (field, value) => {
    if (field.type === "yesno") return value === "yes" ? t("نعم", "Yes") : t("لا", "No");
    if (field.type === "mode") {
      return value === "pos" ? "POS" : value === "external_tablet" ? t("تابلت", "Tablet") : "";
    }
    return value;
  };

  const renderInput = (field) => {
    const label = ar ? field.ar : field.en;
    const error = errors[field.key];
    if (field.type === "yesno") {
      return (
        <Select
          key={field.key}
          label={label}
          value={draft.hasIntegration}
          onChange={(e) => setDraft((d) => ({ ...d, hasIntegration: e.target.value }))}
          options={[
            { value: "yes", label: t("نعم", "Yes") },
            { value: "no", label: t("لا", "No") },
          ]}
        />
      );
    }
    if (field.type === "mode") {
      return (
        <Select
          key={field.key}
          label={label}
          value={draft.integrationType || ""}
          placeholder={t("اختر", "Select")}
          onChange={(e) => setDraft((d) => ({ ...d, integrationType: e.target.value }))}
          options={[
            { value: "external_tablet", label: t("تابلت", "Tablet") },
            { value: "pos", label: "POS" },
          ]}
        />
      );
    }
    return (
      <Input
        key={field.key}
        type={field.type}
        label={label}
        value={draft[field.key] || ""}
        error={error}
        onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
      />
    );
  };

  const renderSection = (section) => {
    const isEditing = editing === section.id;
    const view = isEditing ? { ...committed, ...draft } : committed;
    const groups = section.groups || [section];
    const applicable = section.fields.filter((f) => !f.when || f.when(view));
    const missingCount = section.optional
      ? 0
      : applicable.filter((f) => !f.always && !f.optional && fieldStatus(f, committed) !== "complete").length;
    const headStatus = section.optional ? "optional" : missingCount ? "missing" : "complete";
    return (
      <Card
        key={section.id}
        className={`lud-completion-section ${section.optional ? "is-optional" : ""} ${section.id === "business" ? "is-wide" : ""}`}
      >
        <div
          className="lud-completion-section-head"
          ref={(node) => { sectionRefs.current[section.id] = node; }}
        >
          <h3>{ar ? section.ar : section.en}</h3>
          <div className="lud-completion-section-actions">
            {section.optional ? (
              <StatusTag status="optional" lang={lang} />
            ) : (
              <Tags variant={headStatus === "complete" ? "success" : "danger"} appearance="soft" size="sm">
                {headStatus === "complete"
                  ? t("مكتمل", "Complete")
                  : ar ? `${missingCount} ناقص` : `${missingCount} missing`}
              </Tags>
            )}
            {!isEditing && (
              <Button type="button" size="sm" appearance="outline" disabled={readOnly} onClick={() => startEdit(section)}>
                {t("تعديل", "Edit")}
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="lud-completion-edit">
            {groups.map((group) => (
              <div key={group.id} className="lud-completion-group">
                {section.groups && <h4 className="lud-completion-group-title">{ar ? group.ar : group.en}</h4>}
                <div className="lud-completion-fields">
                  {group.fields.filter((f) => !f.when || f.when(view)).map((field) => renderInput(field))}
                </div>
              </div>
            ))}
            {sectionError && <p className="lud-completion-error" role="alert">{sectionError}</p>}
            <div className="lud-completion-edit-actions">
              <Button type="button" disabled={saving} onClick={() => saveSection(section)}>
                {saving ? t("جاري الحفظ...", "Saving...") : t("حفظ", "Save")}
              </Button>
              <Button type="button" appearance="outline" disabled={saving} onClick={cancelEdit}>
                {t("إلغاء", "Cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {groups.map((group) => (
              <div key={group.id} className="lud-completion-group">
                {section.groups && <h4 className="lud-completion-group-title">{ar ? group.ar : group.en}</h4>}
                <dl className="lud-completion-rows">
                  {group.fields.filter((f) => !f.when || f.when(view)).map((field) => {
                    const status = fieldStatus(field, committed, section.optional);
                    const value = renderValue(field, committed[field.key]);
                    return (
                      <div
                        key={field.key}
                        className={`lud-completion-row ${status === "missing" || status === "invalid" ? "is-problem" : ""}`}
                      >
                        <dt>{ar ? field.ar : field.en}</dt>
                        <dd>
                          <span className="lud-completion-value" dir="auto">
                            {isEmpty(value) ? "—" : value}
                          </span>
                          {status !== "complete" && <StatusTag status={status} lang={lang} />}
                          {status === "invalid" && field.msg && (
                            <small className="lud-completion-hint">{ar ? field.msg[0] : field.msg[1]}</small>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            ))}
          </>
        )}
      </Card>
    );
  };

  const documentsMissing = docInfo.filter((d) => !d.optional && !d.file).length;

  const missingGroups = [];
  completion.missing.forEach((item) => {
    let group = missingGroups.find((g) => g.id === item.section);
    if (!group) {
      group = { id: item.section, ar: item.sectionAr, en: item.sectionEn, items: [] };
      missingGroups.push(group);
    }
    group.items.push(item);
  });

  const renderAccordion = ({ id, open, toggle, icon, title, subtitle, tags, body }) => (
    <div className={`lud-completion-acc ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="lud-completion-acc-head"
        id={`completion-acc-${id}-head`}
        aria-expanded={open}
        aria-controls={`completion-acc-${id}-panel`}
        onClick={toggle}
      >
        <span className="lud-completion-acc-icon" aria-hidden="true">{icon}</span>
        <span className="lud-completion-acc-title">
          <strong>{title}</strong>
          {subtitle && <small>{subtitle}</small>}
        </span>
        <span className="lud-completion-acc-tags">{tags}</span>
        <ChevronDown size={18} className="lud-completion-acc-chevron" aria-hidden="true" />
      </button>
      <div
        className="lud-completion-acc-panel"
        id={`completion-acc-${id}-panel`}
        role="region"
        aria-labelledby={`completion-acc-${id}-head`}
        aria-hidden={!open}
        inert={!open}
      >
        <div className="lud-completion-acc-inner">{body}</div>
      </div>
    </div>
  );

  return (
    <section className="lud-dashboard-section">
      <Card className="lud-completion-card">
        {needsSubscription && (
          <div className="lud-completion-subscription" data-testid="completion-subscription">
            <span>{t("لا يوجد اشتراك فعّال لهذا المتجر بعد.", "This store does not have an active subscription yet.")}</span>
            <BillingButton label={t("الاشتراك والفوترة", "Subscription & Billing")} />
          </div>
        )}
        <div className="lud-dashboard-section-title">
          <h2>{text?.completion || t("اكتمال البيانات", "Completion")}</h2>
          <strong>{completion.percent}%</strong>
        </div>
        <ProgressBar
          value={completion.percent}
          max={100}
          label={text?.completion || "Completion"}
        />
        <p className="lud-completion-summary">
          {ar
            ? `${completion.completed} من ${completion.total} عنصر مطلوب مكتمل`
            : `${completion.completed} of ${completion.total} required items complete`}
        </p>

        <div className="lud-completion-accordions">
          {renderAccordion({
            id: "missing",
            open: openMissing,
            toggle: () => setOpenMissing((v) => !v),
            icon: <AlertTriangle size={18} />,
            title: t("المعلومات الناقصة", "Missing Information"),
            tags: (
              <Tags
                variant={completion.missing.length ? "danger" : "success"}
                appearance="soft"
                size="sm"
              >
                {completion.missing.length
                  ? ar ? `${completion.missing.length} ناقص` : `${completion.missing.length} missing`
                  : t("مكتمل", "Complete")}
              </Tags>
            ),
            body: completion.missing.length ? (
              <div className="lud-completion-missing-groups">
                {missingGroups.map((group) => (
                  <div key={group.id} className="lud-completion-missing-group">
                    <h4>{ar ? group.ar : group.en}</h4>
                    <div className="lud-completion-missing">
                      {group.items.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className="lud-completion-chip"
                          onClick={() => jumpTo(item.section)}
                        >
                          <Tags
                            variant={item.status === "invalid" ? "warning" : "danger"}
                            appearance="soft"
                            size="sm"
                          >
                            {ar ? item.field.ar : item.field.en}
                          </Tags>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="lud-completion-empty">
                {t("كل المعلومات المطلوبة مكتملة.", "All required information is complete.")}
              </p>
            ),
          })}

          {renderAccordion({
            id: "account",
            open: openAccount,
            toggle: () => setOpenAccount((v) => !v),
            icon: <Building2 size={18} />,
            title: t("معلومات الحساب", "Account Information"),
            subtitle: ar
              ? `${completion.completed} من ${completion.total} مكتمل`
              : `${completion.completed} of ${completion.total} complete`,
            tags: missingGroups.length ? (
              missingGroups.map((group) => (
                <Tags key={group.id} variant="danger" appearance="soft" size="sm">
                  {`${ar ? group.ar : group.en}: ${group.items.length}`}
                </Tags>
              ))
            ) : (
              <Tags variant="success" appearance="soft" size="sm">
                {t("مكتمل", "Complete")}
              </Tags>
            ),
            body: (
              <>
        <div className="lud-completion-grid">
          <div className="lud-completion-col lud-completion-col-main">
            {CARDS.filter((section) => section.id === "business").map(renderSection)}
          </div>
          <div className="lud-completion-col lud-completion-col-side">
            {CARDS.filter((section) => section.id !== "business").map(renderSection)}
          </div>
        </div>

        <div className="lud-completion-grid-footer">
          <Card className="lud-completion-section lud-completion-docs">
            <div
              className="lud-completion-section-head"
              ref={(node) => { sectionRefs.current.documents = node; }}
            >
              <h3>{t("المستندات", "Documents")}</h3>
              <div className="lud-completion-section-actions">
                <Tags variant={documentsMissing ? "danger" : "success"} appearance="soft" size="sm">
                  {documentsMissing
                    ? ar ? `${documentsMissing} ناقص` : `${documentsMissing} missing`
                    : t("مكتمل", "Complete")}
                </Tags>
              </div>
            </div>
            {docError && <p className="lud-completion-error" role="alert">{docError}</p>}
            <ul className="lud-completion-doc-list">
              {docInfo.map((doc) => {
                const busy = busyDoc === doc.key;
                const showImage = doc.image && doc.url && (doc.source === "banner" || isImageFile(doc.file));
                return (
                  <li
                    key={doc.key}
                    className={`lud-completion-doc ${doc.file || doc.optional ? "" : "is-problem"}`}
                  >
                    <div className="lud-completion-doc-main">
                      {showImage ? (
                        <img className="lud-completion-doc-thumb" src={doc.url} alt="" />
                      ) : (
                        <span className="lud-completion-doc-icon"><FileText size={18} /></span>
                      )}
                      <div className="lud-completion-doc-text">
                        <strong>{ar ? doc.ar : doc.en}</strong>
                        {doc.file && (
                          <span className="lud-completion-doc-file" dir="auto">
                            {doc.file.originalName || doc.file.name || ""}
                          </span>
                        )}
                      </div>
                      <StatusTag status={doc.file ? "complete" : doc.optional ? "optional" : "missing"} lang={lang} />
                    </div>
                    <div className="lud-completion-doc-actions">
                      {doc.file && (
                        <Button type="button" size="sm" appearance="outline" disabled={busy} onClick={() => viewDoc(doc)}>
                          {t("عرض", "View")}
                        </Button>
                      )}
                      {canManageFiles && (
                        <>
                          <input
                            ref={(node) => { fileInputs.current[doc.key] = node; }}
                            type="file"
                            hidden
                            accept={doc.image ? "image/*" : ".pdf,image/*"}
                            aria-label={ar ? doc.ar : doc.en}
                            data-testid={`completion-file-${doc.key}`}
                            onChange={(event) => {
                              uploadDoc(doc, event.target.files?.[0]);
                              event.target.value = "";
                            }}
                          />
                          <Button
                            type="button"
                            size="sm"
                            disabled={busy}
                            onClick={() => fileInputs.current[doc.key]?.click()}
                          >
                            <Upload size={14} />{" "}
                            {busy
                              ? t("جاري الرفع...", "Working...")
                              : doc.file ? t("استبدال", "Replace") : t("رفع", "Upload")}
                          </Button>
                          {doc.file && (
                            <Button type="button" size="sm" variant="danger" appearance="outline" disabled={busy} onClick={() => deleteDoc(doc)}>
                              {t("حذف", "Delete")}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
              </>
            ),
          })}
        </div>
      </Card>
    </section>
  );
}
